import React from "react";
import { Button, Form, Input, Modal, Space, Table, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import Base from "../Base";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { BreadCrumb } from "../../components/main/LayoutComponents";
import TranslateBtn from "../../components/product/TranslateBtn";

class ProductUnitList extends Base {
  state = {
    loading: false,
    units: [],
    modalOpen: false,
    editing: null,
    saving: false,
  };

  componentDidMount() {
    super.componentDidMount();
    this.loadUnits();
  }

  loadUnits = async () => {
    const { user } = this.props;
    this.setState({ loading: true });
    try {
      const res = await this.api.product.productUnits.run({
        token: user.token,
      });
      if (res.result) {
        this.setState({ units: res.data || [] });
      } else {
        this.showMessage(res.message, Base.Message.error);
      }
    } catch (e) {
      this.showMessage(e.message, Base.Message.error);
    } finally {
      this.setState({ loading: false });
    }
  };

  openCreate = () => {
    this.setState({ modalOpen: true, editing: null });
    setTimeout(() => this.formRef?.resetFields(), 0);
  };

  openEdit = (record) => {
    this.setState({ modalOpen: true, editing: record });
    setTimeout(() => {
      this.formRef?.setFieldsValue({
        ug_name: record.ug_name,
        zh_name: record.zh_name,
      });
    }, 0);
  };

  handleSave = async () => {
    const { user } = this.props;
    const { editing } = this.state;
    try {
      const values = await this.formRef.validateFields();
      this.setState({ saving: true });
      const payload = {
        token: user.token,
        data: {
          ug_name: values.ug_name.trim(),
          zh_name: values.zh_name.trim(),
        },
      };
      const res = editing
        ? await this.api.product.updateProductUnit.run({
            ...payload,
            path: { id: editing.id },
          })
        : await this.api.product.addProductUnit.run(payload);

      if (res.result) {
        this.showMessage(
          this.i18n.t("productUnit.saveSuccess"),
          Base.Message.success
        );
        this.setState({ modalOpen: false, editing: null });
        this.loadUnits();
      } else {
        this.showMessage(res.message, Base.Message.error);
      }
    } catch (e) {
      if (e?.errorFields) return;
      this.showMessage(e.message, Base.Message.error);
    } finally {
      this.setState({ saving: false });
    }
  };

  handleDelete = (record) => {
    const { user } = this.props;
    Modal.confirm({
      title: this.i18n.t("productUnit.deleteConfirm"),
      onOk: async () => {
        const res = await this.api.product.deleteProductUnit.run({
          token: user.token,
          path: { id: record.id },
        });
        if (res.result) {
          this.showMessage(
            this.i18n.t("productUnit.deleteSuccess"),
            Base.Message.success
          );
          this.loadUnits();
        } else {
          this.showMessage(res.message, Base.Message.error);
        }
      },
    });
  };

  render() {
    const { loading, units, modalOpen, editing, saving } = this.state;
    const columns = [
      {
        title: "ID",
        dataIndex: "id",
        width: 80,
      },
      {
        title: this.i18n.t("productUnit.zhName"),
        dataIndex: "zh_name",
      },
      {
        title: this.i18n.t("productUnit.ugName"),
        dataIndex: "ug_name",
      },
      {
        title: this.i18n.t("productUnit.actions"),
        width: 180,
        render: (_, record) => (
          <Space>
            <Button type="link" onClick={() => this.openEdit(record)}>
              {this.i18n.t("productUnit.editAction")}
            </Button>
            <Button type="link" danger onClick={() => this.handleDelete(record)}>
              {this.i18n.t("productUnit.deleteAction")}
            </Button>
          </Space>
        ),
      },
    ];

    return (
      <div className="product-unit-list">
        <BreadCrumb
          data={[
            { title: this.i18n.t("main.sideMenus.product") },
            { title: this.i18n.t("main.sideMenus.productUnits") },
          ]}
        />
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={this.openCreate}>
            {this.i18n.t("productUnit.add")}
          </Button>
        </div>
        <Spin spinning={loading}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={units}
            pagination={false}
          />
        </Spin>
        <Modal
          open={modalOpen}
          title={
            editing
              ? this.i18n.t("productUnit.edit")
              : this.i18n.t("productUnit.add")
          }
          onCancel={() => this.setState({ modalOpen: false, editing: null })}
          onOk={this.handleSave}
          confirmLoading={saving}
          destroyOnClose
        >
          <Form
            ref={(ref) => {
              this.formRef = ref;
            }}
            layout="vertical"
          >
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="zh_name"
                  label={this.i18n.t("productUnit.zhName")}
                  rules={[
                    {
                      required: true,
                      message: this.i18n.t("productUnit.zhNameHint"),
                    },
                  ]}
                >
                  <Input
                    allowClear
                    placeholder={this.i18n.t("productUnit.zhNameHint")}
                    addonAfter={
                      <TranslateBtn
                        context={getFieldValue("zh_name")}
                        onFinish={(value) =>
                          setFieldsValue({
                            ug_name: value,
                          })
                        }
                        from="zh"
                        to="ug"
                      />
                    }
                  />
                </Form.Item>
              )}
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="ug_name"
                  label={this.i18n.t("productUnit.ugName")}
                  rules={[
                    {
                      required: true,
                      message: this.i18n.t("productUnit.ugNameHint"),
                    },
                  ]}
                >
                  <Input
                    allowClear
                    dir="rtl"
                    placeholder={this.i18n.t("productUnit.ugNameHint")}
                    addonAfter={
                      <TranslateBtn
                        context={getFieldValue("ug_name")}
                        onFinish={(value) =>
                          setFieldsValue({
                            zh_name: value,
                          })
                        }
                        from="ug"
                        to="zh"
                      />
                    }
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(ProductUnitList);
